import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import type { ScreenKey } from "../shell/routes.js";
import HomeScreen from "./HomeScreen.js";
import SignInScreen from "./SignInScreen.js";
import AccountScreen from "./AccountScreen.js";
import RoleManagementScreen from "./RoleManagementScreen.js";
import LoginScreen from "./LoginScreen.js";
import MfaChallengeScreen from "./MfaChallengeScreen.js";
import MfaEnrollmentScreen from "./MfaEnrollmentScreen.js";
import RegistrationScreen from "./RegistrationScreen.js";
import EmailVerificationScreen from "./EmailVerificationScreen.js";
import ImpersonationConsentScreen from "./ImpersonationConsentScreen.js";
import DataExportScreen from "./DataExportScreen.js";
import AccountDeletionScreen from "./AccountDeletionScreen.js";

/**
 * Screen registry — resolves each {@link ScreenKey} to its screen component,
 * with a deliberate split between the server and client render paths.
 *
 * Why two maps:
 *   - `SERVER_SCREENS` uses EAGER (statically imported) components. React's
 *     `renderToString` is synchronous and cannot await a `React.lazy` chunk on
 *     the server — a lazy screen would render its Suspense fallback instead of
 *     real content. Rendering eagerly means the server HTML contains the actual
 *     screen markup alongside the layout + navigation (Req 21.1). Because these
 *     are static imports resolved at build time, no bundler is required to
 *     produce server-side screen content.
 *   - `CLIENT_SCREENS` uses `React.lazy` with dynamic `import()`, giving
 *     route-level code splitting in the browser: a bundler emits one chunk per
 *     screen and the client fetches only the current route's chunk, loading
 *     others on demand as the user navigates.
 *
 * Both maps are keyed by the same {@link ScreenKey}, so the server-rendered
 * tree and the hydrated tree render the identical component for a route — only
 * the loading strategy differs. The shell selects the correct map via its
 * `mode` prop.
 *
 * Later screen tasks (Req 22-25) add their screen module to BOTH maps and a
 * matching route in `routes.ts`; nothing else in the shell changes.
 */

/** Eager screens for the server render path (real content in SSR HTML). */
export const SERVER_SCREENS: Readonly<Record<ScreenKey, ComponentType>> = {
  home: HomeScreen,
  signIn: SignInScreen,
  account: AccountScreen,
  login: LoginScreen,
  mfaChallenge: MfaChallengeScreen,
  mfaEnrollment: MfaEnrollmentScreen,
  register: RegistrationScreen,
  verifyEmail: EmailVerificationScreen,
  roleManagement: RoleManagementScreen as ComponentType,
  impersonationConsent: ImpersonationConsentScreen as ComponentType,
  dataExport: DataExportScreen as ComponentType,
  accountDeletion: AccountDeletionScreen as ComponentType,
};

/** Lazy, code-split screens for the client hydration path. */
export const CLIENT_SCREENS: Readonly<
  Record<ScreenKey, LazyExoticComponent<ComponentType>>
> = {
  home: lazy(() => import("./HomeScreen.js")),
  signIn: lazy(() => import("./SignInScreen.js")),
  account: lazy(() => import("./AccountScreen.js")),
  login: lazy(() => import("./LoginScreen.js")),
  mfaChallenge: lazy(() => import("./MfaChallengeScreen.js")),
  mfaEnrollment: lazy(() => import("./MfaEnrollmentScreen.js")),
  register: lazy(() => import("./RegistrationScreen.js")),
  verifyEmail: lazy(() => import("./EmailVerificationScreen.js")),
  roleManagement: lazy(
    () => import("./RoleManagementScreen.js") as Promise<{ default: ComponentType }>,
  ),
  impersonationConsent: lazy(
    () =>
      import("./ImpersonationConsentScreen.js") as Promise<{
        default: ComponentType;
      }>,
  ),
  dataExport: lazy(
    () => import("./DataExportScreen.js") as Promise<{ default: ComponentType }>,
  ),
  accountDeletion: lazy(
    () =>
      import("./AccountDeletionScreen.js") as Promise<{ default: ComponentType }>,
  ),
};
